import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    UploadCloud, Briefcase, Info, TrendingUp, CheckCircle2, 
    AlertTriangle, AlertCircle, FileText, Send, Sparkles, MessageSquare,
    Copy, Check, RotateCw, Star, Loader2, Target
} from 'lucide-react';
import { CircularProgress, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api, { getResumeAnalysis, runResumeAnalysis } from '../../../../api/client';
import PremiumSelect from '../../component/PremiumSelect';
import PageHeader, { PageCard, PageHeaderButton, PageShell } from '../../../common/PageHeader';

const CustomLinearProgress = ({ value, colorClass }) => (
    <div className="flex items-center w-full">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full ${colorClass} rounded-full transition-all duration-700`} style={{ width: `${value}%` }}></div>
        </div>
        <div className="w-10 text-right text-xs font-bold text-gray-700 ml-3">{value}%</div>
    </div>
);

// Skeleton loader for cards
const CardSkeleton = ({ className = '' }) => (
    <div className={`bg-white border border-gray-100 rounded-2xl p-6 shadow-sm animate-pulse ${className}`}>
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-3 bg-gray-100 rounded w-full mb-2"></div>
        <div className="h-3 bg-gray-100 rounded w-2/3 mb-2"></div>
        <div className="h-3 bg-gray-100 rounded w-1/2"></div>
    </div>
);

const ResumeAnalyzePage = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState('ats');
    const [activeImprovementTab, setActiveImprovementTab] = useState('summary');
    const [copied, setCopied] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [analysis, setAnalysis] = useState(null);
    const [error, setError] = useState(null);

    // JD State
    const [jds, setJds] = useState([]);
    const [selectedJdId, setSelectedJdId] = useState('');
    const [isFetchingJds, setIsFetchingJds] = useState(false);

    // Grouped options for PremiumSelect
    const jobOptions = useMemo(() => {
        const list = [{ value: '', label: t('b2cDashboard.resumeAnalyzePage.selectJobPlaceholder', '-- Select a Job --') }];

        const applied = jds.filter(jd => jd.source === 'applied');
        if (applied.length > 0) {
            list.push({
                group: t('b2cDashboard.resumeAnalyzePage.appliedJobs', 'Applied Jobs'),
                options: applied.map(jd => ({ value: String(jd.id), label: jd.title }))
            });
        }

        const recent = jds.filter(jd => jd.source === 'recent');
        if (recent.length > 0) {
            list.push({
                group: t('b2cDashboard.resumeAnalyzePage.recentJobs', 'Recent Jobs'),
                options: recent.map(jd => ({ value: String(jd.id), label: jd.title }))
            });
        }

        const practice = jds.filter(jd => jd.source === 'practice');
        if (practice.length > 0) {
            list.push({
                group: t('b2cDashboard.resumeAnalyzePage.practiceRoles', 'Practice Roles'),
                options: practice.map(jd => ({ value: String(jd.id), label: jd.title }))
            });
        }

        return list;
    }, [jds, t]);

    // Upload state
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx)$/i)) {
            toast.error(t('b2cDashboard.resumeAnalyzePage.pleaseUploadPdfWord', 'Please upload a PDF or Word document.'));
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            await api.post('resumes/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(t('b2cDashboard.resumeAnalyzePage.uploadSuccess', 'Resume uploaded successfully. Analyzing new resume...'));
            await handleRunAnalysis(selectedJdId);
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error(t('b2cDashboard.resumeAnalyzePage.uploadFailed', 'Failed to upload resume. Please try again.'));
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchCachedAnalysis();
        fetchJobDescriptions();
    }, []);

    const fetchJobDescriptions = async () => {
        try {
            setIsFetchingJds(true);
            const res = await api.get('candidate/job-match-options/');
            setJds(res.data || []);
        } catch (err) {
            console.error("Failed to fetch job match options", err);
        } finally {
            setIsFetchingJds(false);
        }
    };

    const fetchCachedAnalysis = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getResumeAnalysis();
            setAnalysis(data);
        } catch (err) {
            if (err?.response?.status === 404) {
                // No cached analysis — that's OK, show empty state
                setAnalysis(null);
            } else {
                setError(t('b2cDashboard.resumeAnalyzePage.loadFailed', 'Failed to load analysis. Please try again.'));
                console.error('Failed to fetch analysis:', err);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRunAnalysis = async (jdId = null) => {
        setIsAnalyzing(true);
        setError(null);
        try {
            const data = await runResumeAnalysis(null, jdId);
            setAnalysis(data);
            toast.success(t('b2cDashboard.resumeAnalyzePage.analysisCompleted', 'Resume analysis completed!'));
            if (jdId) {
                setActiveTab('skills');
            }
        } catch (err) {
            const msg = err?.response?.data?.error || t('b2cDashboard.resumeAnalyzePage.analysisFailed', 'Analysis failed. Please try again.');
            toast.error(msg);
            setError(msg);
            console.error('Analysis failed:', err);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success(t('b2cDashboard.resumeAnalyzePage.copiedClipboard', 'Copied to clipboard!'));
        setTimeout(() => setCopied(false), 2000);
    };

    const formatDate = (isoString) => {
        if (!isoString) return t('b2cDashboard.resumeAnalyzePage.never', 'Never');
        return new Date(isoString).toLocaleString(i18n.language === 'en' ? 'en-US' : (i18n.language === 'hi' ? 'hi-IN' : i18n.language), {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        });
    };

    // Helper to get score color
    const getScoreColor = (score) => {
        if (score >= 80) return '#22c55e';
        if (score >= 60) return '#f59e0b';
        if (score >= 40) return '#f97316';
        return '#ef4444';
    };

    const getScoreLabel = (score) => {
        if (score >= 80) return t('b2cDashboard.resumeAnalyzePage.scoreLabelExcellent', 'Excellent');
        if (score >= 60) return t('b2cDashboard.resumeAnalyzePage.scoreLabelGood', 'Good Score');
        if (score >= 40) return t('b2cDashboard.resumeAnalyzePage.scoreLabelImprovement', 'Needs Improvement');
        return t('b2cDashboard.resumeAnalyzePage.scoreLabelPoor', 'Poor Score');
    };

    const getStatusIcon = (s) => {
        if (s === 'Present') return <CheckCircle2 className="w-4 h-4 text-green-500" />;
        if (s === 'Missing') return <AlertTriangle className="w-4 h-4 text-red-500" />;
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
    };

    const getStatusColor = (s) => {
        if (s === 'Present') return 'text-green-600';
        if (s === 'Missing') return 'text-red-500';
        return 'text-orange-500';
    };

    const getProgressBarColor = (s) => {
        if (s === 'Matched') return 'bg-green-500';
        if (s === 'Partial') return 'bg-orange-400';
        return 'bg-red-400';
    };

    // Safely read data from analysis
    const atsScore = analysis?.ats_score || 0;
    const qualityScore = analysis?.quality_score || 0;
    const atsBreakdown = analysis?.ats_breakdown || {};
    const atsCheckResults = analysis?.ats_check_results || [];
    const atsIssues = analysis?.ats_issues || { high: [], medium: [], low: [] };
    const improvements = analysis?.improvement_suggestions || {};
    const missingSkillsData = analysis?.missing_skills || [];
    const strengths = analysis?.strengths || [];
    const skillsMatch = analysis?.skills_match || [];
    const jobMatchScore = analysis?.job_match_score;
    const resumeFilename = analysis?.resume_filename || 'Resume';

    const tabs = [
        { id: 'ats', label: t('b2cDashboard.resumeAnalyzePage.tabs.ats', 'ATS Analysis'), icon: <img src="https://img.icons8.com/fluency/48/approval.png" alt="ats" className="w-4 h-4 mr-2" /> },
        { id: 'ai', label: t('b2cDashboard.resumeAnalyzePage.tabs.ai', 'AI Improvements'), icon: <Sparkles className="w-4 h-4 mr-2" /> },
        { id: 'skills', label: t('b2cDashboard.resumeAnalyzePage.tabs.match', 'Job Match'), icon: <Target className="w-4 h-4 mr-2" />, badge: t('b2cDashboard.resumeAnalyzePage.tabs.new', 'NEW') },
    ];

    const improvementTabs = [
        { id: 'summary', label: t('b2cDashboard.resumeAnalyzePage.tabs.summary', 'Professional Summary'), icon: <FileText className="w-4 h-4" /> },
        { id: 'experience', label: t('b2cDashboard.resumeAnalyzePage.tabs.experience', 'Work Experience'), icon: <Briefcase className="w-4 h-4" /> },
        { id: 'projects', label: t('b2cDashboard.resumeAnalyzePage.tabs.projects', 'Projects'), icon: <Briefcase className="w-4 h-4" /> },
        { id: 'skills', label: t('b2cDashboard.resumeAnalyzePage.tabs.skills', 'Skills'), icon: <Sparkles className="w-4 h-4" /> },
        { id: 'education', label: t('b2cDashboard.resumeAnalyzePage.tabs.education', 'Education'), icon: <img src="https://img.icons8.com/color/48/graduation-cap.png" alt="edu" className="w-4 h-4" /> },
    ];

    // Empty state — no analysis and not loading
    if (!isLoading && !analysis && !error) {
        return (
            <PageShell fill={false} style={{ background: '#f8fafc', fontFamily: "'Poppins', sans-serif" }}>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".pdf,.doc,.docx" className="hidden" />
                <PageCard overflow={false}>
                        <PageHeader
                            title={t('b2cDashboard.resumeAnalyzePage.title', 'Resume Analyze')}
                            subtitle={t('b2cDashboard.resumeAnalyzePage.subtitle', 'Get AI-powered insights to improve your resume and match better with jobs.')}
                        />
                    <div className="p-12 text-center">
                        <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FileText className="w-8 h-8 text-indigo-400" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">{t('b2cDashboard.resumeAnalyzePage.noAnalysisYet', 'No Analysis Yet')}</h2>
                        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                            {t('b2cDashboard.resumeAnalyzePage.noAnalysisDescription', 'Run your first AI-powered resume analysis to get ATS scoring, quality insights, improvement suggestions, and more.')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={() => handleRunAnalysis()}
                                disabled={isAnalyzing || isUploading}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-xl shadow-sm transition-all hover:shadow-md flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                            >
                                {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                {isAnalyzing ? t('b2cDashboard.resumeAnalyzePage.analyzing', 'Analyzing...') : t('b2cDashboard.resumeAnalyzePage.analyzeLatestResume', 'Analyze Latest Resume')}
                            </button>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isAnalyzing || isUploading}
                                className="bg-white hover:bg-gray-50 text-indigo-600 font-bold px-6 py-3 rounded-xl shadow-sm border border-gray-200 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                            >
                                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                                {isUploading ? t('b2cDashboard.resumeAnalyzePage.uploading', 'Uploading...') : t('b2cDashboard.resumeAnalyzePage.uploadNewResume', 'Upload New Resume')}
                            </button>
                        </div>
                    </div>
                </PageCard>
            </PageShell>
        );
    }

    return (
        <PageShell fill={false} style={{ background: '#f8fafc', fontFamily: "'Poppins', sans-serif" }}>
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".pdf,.doc,.docx" className="hidden" />
            <PageCard overflow={false}>
                    <PageHeader
                        title={
                            <>
                                {t('b2cDashboard.resumeAnalyzePage.title', 'Resume Analyze')}
                                {isAnalyzing && <RotateCw className="h-5 w-5 animate-spin text-white" />}
                            </>
                        }
                        subtitle={t('b2cDashboard.resumeAnalyzePage.subtitle', 'Get AI-powered insights to improve your resume and match better with jobs.')}
                        extra={
                            analysis ? (
                                <p className="mt-1 text-[10px] font-semibold text-white/80">
                                    📄 {resumeFilename} · {t('b2cDashboard.resumeAnalyzePage.lastAnalyzed', 'Last Analyzed')}: {formatDate(analysis.analyzed_at)}
                                </p>
                            ) : null
                        }
                    >
                        <PageHeaderButton
                            onClick={() => handleRunAnalysis(analysis?.job_description_id)}
                            disabled={isAnalyzing || isUploading}
                            icon={<RotateCw className={`h-4 w-4 ${isAnalyzing ? 'animate-spin' : ''}`} />}
                        >
                            {isAnalyzing ? t('b2cDashboard.resumeAnalyzePage.analyzing', 'Analyzing...') : t('b2cDashboard.resumeAnalyzePage.reAnalyzeResume', 'Re-analyze Resume')}
                        </PageHeaderButton>
                        <PageHeaderButton
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isAnalyzing || isUploading}
                            color="indigo"
                            icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                        >
                            {isUploading ? t('b2cDashboard.resumeAnalyzePage.uploading', 'Uploading...') : t('b2cDashboard.resumeAnalyzePage.uploadNewResume', 'Upload New Resume')}
                        </PageHeaderButton>
                    </PageHeader>
                <div className="p-3 sm:p-4">

                {/* Loading State */}
                {isLoading && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        <CardSkeleton />
                        <CardSkeleton />
                        <CardSkeleton />
                    </div>
                )}

                {/* Error State */}
                {error && !isLoading && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-sm text-red-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        {error}
                    </div>
                )}

                {/* Main Content — only when we have analysis data */}
                {analysis && !isLoading && (
                    <>
                        {/* Tabs Container */}
                        <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] border border-gray-100 overflow-hidden mb-6">
                            <div className="flex overflow-x-auto border-b border-gray-100 no-scrollbar px-2">
                                {tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center px-4 py-4 text-sm font-bold whitespace-nowrap transition-colors relative ${
                                            activeTab === tab.id 
                                            ? 'text-indigo-600 border-b-2 border-indigo-600' 
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-b-2 border-transparent'
                                        }`}
                                    >
                                        {tab.icon}
                                        {tab.label}
                                        {tab.badge && (
                                            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-extrabold text-green-700 bg-green-100 border border-green-200 uppercase tracking-wider">
                                                {tab.badge}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* === ATS Analysis Tab === */}
                            {activeTab === 'ats' && (
                                <div className="p-4 sm:p-6 bg-gray-50/50">
                                    {/* ATS Top Row */}
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
                                        {/* ATS Score */}
                                        <div className="col-span-1 lg:col-span-4 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex flex-col justify-between relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-full h-1" style={{ background: `linear-gradient(to right, ${getScoreColor(atsScore)}, ${getScoreColor(atsScore)}80)` }}></div>
                                            <div className="flex items-center gap-1.5 mb-6 text-gray-900 font-extrabold text-base">
                                                {t('b2cDashboard.resumeAnalyzePage.overallAtsScore', 'Overall ATS Score')} <Info className="w-4 h-4 text-gray-400 cursor-help" title={t('b2cDashboard.resumeAnalyzePage.atsScoreHelp', 'ATS score checks parseability, not job fit.')} />
                                            </div>
                                            <div className="flex flex-col sm:flex-row items-center gap-6">
                                                <div className="relative flex items-center justify-center shrink-0 w-[110px] h-[110px]">
                                                    <CircularProgress variant="determinate" value={100} size={110} thickness={6} sx={{ color: '#f1f5f9', position: 'absolute' }} />
                                                    <CircularProgress variant="determinate" value={atsScore} size={110} thickness={6} sx={{ color: getScoreColor(atsScore), position: 'absolute', strokeLinecap: 'round' }} />
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                        <span className="text-3xl font-black text-gray-900 leading-none">{Math.round(atsScore)}</span>
                                                        <span className="text-xs font-bold text-gray-400 mt-1">/100</span>
                                                    </div>
                                                </div>
                                                <div className="text-center sm:text-left">
                                                    <p className="text-xs font-medium text-gray-600 leading-relaxed mb-4">
                                                        {atsScore >= 80 ? t('b2cDashboard.resumeAnalyzePage.atsScoreExcellent', 'Your resume is well-structured and optimized for ATS systems.') :
                                                         atsScore >= 60 ? t('b2cDashboard.resumeAnalyzePage.atsScoreGood', 'Your resume is decent but has areas for improvement to pass ATS filters.') :
                                                         t('b2cDashboard.resumeAnalyzePage.atsScorePoor', 'Your resume needs significant improvements to pass ATS filters.')}
                                                    </p>
                                                    {atsScore >= 70 && (
                                                        <div className="inline-flex items-center px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-bold border border-green-100 shadow-sm">
                                                            {t('b2cDashboard.resumeAnalyzePage.topCandidates', 'Top {{percentage}}% of candidates', { percentage: 100 - Math.round(atsScore) })} <TrendingUp className="w-3.5 h-3.5 ml-1" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-5 font-extrabold text-sm flex justify-center" style={{ color: getScoreColor(atsScore) }}>
                                                {getScoreLabel(atsScore)}
                                            </div>
                                        </div>

                                        {/* ATS Score Breakdown */}
                                        <div className="col-span-1 lg:col-span-4 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-indigo-500"></div>
                                            <div className="flex items-center gap-1.5 mb-6 text-gray-900 font-extrabold text-base">
                                                {t('b2cDashboard.resumeAnalyzePage.atsBreakdown', 'ATS Score Breakdown')} <Info className="w-4 h-4 text-gray-400" />
                                            </div>
                                            <div className="space-y-4">
                                                {Object.entries(atsBreakdown).map(([key, value]) => (
                                                    <div key={key} className="flex items-center justify-between text-sm">
                                                        <span className="text-gray-700 font-semibold w-1/2 capitalize">{key.replace(/_/g, ' ')}</span>
                                                        <CustomLinearProgress value={value || 0} colorClass={value >= 70 ? 'bg-green-500' : value >= 40 ? 'bg-orange-400' : 'bg-red-400'} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* ATS Check Results */}
                                        <div className="col-span-1 lg:col-span-4 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-red-500"></div>
                                            <div className="flex items-center gap-1.5 mb-5 text-gray-900 font-extrabold text-base">
                                                {t('b2cDashboard.resumeAnalyzePage.atsCheckResults', 'ATS Check Results')} <Info className="w-4 h-4 text-gray-400" />
                                            </div>
                                            <ul className="space-y-3.5">
                                                {atsCheckResults.map((item, idx) => (
                                                    <li key={idx} className="flex items-center justify-between text-sm">
                                                        <div className="flex items-center gap-2 text-gray-700 font-semibold">
                                                            {getStatusIcon(item.status)} {item.label}
                                                        </div>
                                                        <span className={`font-bold text-[11px] ${getStatusColor(item.status)}`}>
                                                            {item.status === 'Present' ? t('b2cDashboard.resumeAnalyzePage.present', 'Present') : item.status === 'Missing' ? t('b2cDashboard.resumeAnalyzePage.missing', 'Missing') : item.status}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Middle Row */}
                                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                                        {/* ATS Issues */}
                                        <div className="col-span-1 xl:col-span-4 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                                            <div className="flex items-center gap-1.5 mb-5 text-gray-900 font-extrabold text-base">
                                                {t('b2cDashboard.resumeAnalyzePage.atsIssues', 'ATS Issues')} <Info className="w-4 h-4 text-gray-400" />
                                            </div>
                                            {(atsIssues.high || []).length > 0 && (
                                                <div className="mb-5">
                                                    <h4 className="text-red-500 font-bold text-xs uppercase tracking-wider mb-3">{t('b2cDashboard.resumeAnalyzePage.highPriority', 'High Priority')}</h4>
                                                    <ul className="space-y-2.5">
                                                        {atsIssues.high.map((issue, idx) => (
                                                            <li key={idx} className="flex items-start gap-2.5">
                                                                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                                                <span className="text-sm text-gray-700 font-medium leading-tight">{issue}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {(atsIssues.medium || []).length > 0 && (
                                                <div className="mb-5">
                                                    <h4 className="text-orange-500 font-bold text-xs uppercase tracking-wider mb-3">{t('b2cDashboard.resumeAnalyzePage.mediumPriority', 'Medium Priority')}</h4>
                                                    <ul className="space-y-2.5">
                                                        {atsIssues.medium.map((issue, idx) => (
                                                            <li key={idx} className="flex items-start gap-2.5">
                                                                <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                                                                <span className="text-sm text-gray-700 font-medium leading-tight">{issue}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {(atsIssues.low || []).length > 0 && (
                                                <div>
                                                    <h4 className="text-blue-500 font-bold text-xs uppercase tracking-wider mb-3">{t('b2cDashboard.resumeAnalyzePage.lowPriority', 'Low Priority')}</h4>
                                                    <ul className="space-y-2.5">
                                                        {atsIssues.low.map((issue, idx) => (
                                                            <li key={idx} className="flex items-start gap-2.5">
                                                                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                                                                <span className="text-sm text-gray-700 font-medium leading-tight">{issue}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>

                                        {/* Strengths & Quality */}
                                        <div className="col-span-1 xl:col-span-8 flex flex-col gap-6">
                                            {/* Strengths */}
                                            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                                                <div className="flex items-center gap-1.5 mb-5 text-gray-900 font-extrabold text-base">
                                                    {t('b2cDashboard.resumeAnalyzePage.resumeStrengths', 'Resume Strengths')} <CheckCircle2 className="w-4 h-4 text-green-500" />
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {strengths.map((strength, idx) => (
                                                        <div key={idx} className="flex items-start gap-2.5 bg-green-50/50 border border-green-100 rounded-xl p-3">
                                                            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                                                            <span className="text-sm text-gray-700 font-medium">{strength}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Resume Quality Score */}
                                            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left flex-1">
                                                <div className="relative flex items-center justify-center shrink-0 w-[90px] h-[90px]">
                                                    <CircularProgress variant="determinate" value={100} size={90} thickness={5} sx={{ color: '#f1f5f9', position: 'absolute' }} />
                                                    <CircularProgress variant="determinate" value={qualityScore} size={90} thickness={5} sx={{ color: getScoreColor(qualityScore), position: 'absolute', strokeLinecap: 'round' }} />
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                        <span className="text-2xl font-black text-gray-900 leading-none">{Math.round(qualityScore)}</span>
                                                        <span className="text-[10px] font-bold text-gray-400 mt-0.5">/100</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="flex items-center justify-center sm:justify-start gap-1.5 mb-2 text-gray-900 font-extrabold text-sm">
                                                        {t('b2cDashboard.resumeAnalyzePage.resumeQualityScore', 'Resume Quality Score')} <Info className="w-3.5 h-3.5 text-gray-400" />
                                                    </div>
                                                    <p className="text-[11px] font-medium text-gray-600 leading-relaxed mb-1">
                                                        {qualityScore >= 80 ? t('b2cDashboard.resumeAnalyzePage.qualityScoreExcellent', 'Excellent resume quality! Well-written and impactful.') :
                                                        qualityScore >= 60 ? t('b2cDashboard.resumeAnalyzePage.qualityScoreGood', 'Good resume. Improve the suggested areas to make it excellent.') :
                                                        t('b2cDashboard.resumeAnalyzePage.qualityScorePoor', 'Your resume needs improvement. Follow the AI suggestions above.')}
                                                    </p>
                                                    <p className="text-[11px] font-bold text-indigo-600">
                                                        {qualityScore >= 80 ? t('b2cDashboard.resumeAnalyzePage.qualityScoreLabelExcellent', 'Outstanding! 🌟') : qualityScore >= 60 ? t('b2cDashboard.resumeAnalyzePage.qualityScoreLabelGood', 'Keep Improving! 🚀') : t('b2cDashboard.resumeAnalyzePage.qualityScoreLabelPoor', "Let's Fix This! 💪")}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* === AI Improvements Tab === */}
                            {activeTab === 'ai' && (
                                <div className="p-4 sm:p-6 bg-gray-50/50">
                                    <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col">
                                        <div className="flex items-center gap-1.5 mb-5 text-gray-900 font-extrabold text-base">
                                            {t('b2cDashboard.resumeAnalyzePage.aiSuggestionsTitle', 'AI-Powered Improvement Suggestions')} <Info className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <div className="flex flex-col lg:flex-row flex-1 border border-gray-100 rounded-xl overflow-hidden">
                                            {/* Left Tabs */}
                                            <div className="w-full lg:w-56 bg-gray-50/80 border-b lg:border-b-0 lg:border-r border-gray-100 flex lg:flex-col overflow-x-auto no-scrollbar">
                                                {improvementTabs.map(tab => (
                                                    <button
                                                        key={tab.id}
                                                        onClick={() => setActiveImprovementTab(tab.id)}
                                                        className={`flex items-center gap-2 sm:gap-3 px-4 py-3.5 text-sm font-semibold transition-colors border-b-4 lg:border-b-0 lg:border-l-4 whitespace-nowrap ${
                                                            activeImprovementTab === tab.id 
                                                            ? 'bg-indigo-50/50 text-indigo-700 border-indigo-600' 
                                                            : 'text-gray-600 border-transparent hover:bg-gray-100'
                                                        }`}
                                                    >
                                                        {React.cloneElement(tab.icon, { className: `w-4 h-4 ${activeImprovementTab === tab.id ? 'text-indigo-600' : 'text-gray-400'}` })}
                                                        {tab.label}
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Right Content */}
                                            <div className="flex-1 p-4 sm:p-5 flex flex-col sm:flex-row gap-5 bg-white relative">
                                                <div className="flex-1 space-y-5">
                                                    {improvements[activeImprovementTab] ? (
                                                        <>
                                                            <div>
                                                                <div className="inline-block bg-red-50 text-red-500 text-[10px] font-bold px-2 py-0.5 rounded border border-red-100 uppercase tracking-wide mb-2">{t('b2cDashboard.resumeAnalyzePage.currentSection', 'Current')}</div>
                                                                <div className="bg-red-50/30 border border-red-100 rounded-lg p-3 text-sm text-gray-700 font-medium">
                                                                    {improvements[activeImprovementTab].current || t('b2cDashboard.resumeAnalyzePage.notFoundInResume', 'Not found in resume')}
                                                                </div>
                                                            </div>
                                                            <div className="relative group">
                                                                <div className="inline-block bg-green-50 text-green-600 text-[10px] font-bold px-2 py-0.5 rounded border border-green-100 uppercase tracking-wide mb-2">{t('b2cDashboard.resumeAnalyzePage.aiSuggestionSection', 'AI Suggestion')}</div>
                                                                <div className="bg-green-50/50 border border-green-200 rounded-lg p-4 text-sm text-gray-800 font-medium leading-relaxed">
                                                                    {improvements[activeImprovementTab].suggested || t('b2cDashboard.resumeAnalyzePage.noSuggestionAvailable', 'No suggestion available')}
                                                                </div>
                                                                <button 
                                                                    onClick={() => handleCopy(improvements[activeImprovementTab].suggested || '')}
                                                                    className="absolute top-8 right-2 p-1.5 rounded-lg bg-white border border-green-200 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 shadow-sm transition-all"
                                                                    title={t('b2cDashboard.resumeAnalyzePage.copySuggestion', 'Copy Suggestion')}
                                                                >
                                                                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-sm text-gray-500 py-8 text-center">
                                                            {t('b2cDashboard.resumeAnalyzePage.noImprovements', 'No improvement suggestions available for this section.')}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Why sidebar */}
                                                {improvements[activeImprovementTab]?.reasons && (
                                                    <div className="w-full sm:w-48 xl:w-56 bg-indigo-50/40 rounded-xl p-4 border border-indigo-100 hidden md:block">
                                                        <h4 className="text-indigo-800 font-bold text-xs mb-3">{t('b2cDashboard.resumeAnalyzePage.whyBetter', 'Why this is better?')}</h4>
                                                        <ul className="space-y-3">
                                                            {improvements[activeImprovementTab].reasons.map((reason, idx) => (
                                                                <li key={idx} className="flex items-start gap-2">
                                                                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                                                                    <span className="text-xs text-gray-700 font-medium leading-tight">{reason}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* === Job Match Tab === */}
                            {activeTab === 'skills' && (
                                <div className="p-4 sm:p-6 bg-gray-50/50">
                                    {/* Selector Area (Always Visible) */}
                                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6 flex flex-col items-center text-center">
                                        {!analysis?.job_description_id && (
                                            <>
                                                <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
                                                    <Target className="w-8 h-8 text-orange-400" />
                                                </div>
                                                <h2 className="text-lg font-bold text-gray-900 mb-2">{t('b2cDashboard.resumeAnalyzePage.compareResumeAgainstJob', 'Compare Your Resume Against a Job')}</h2>
                                                <p className="text-gray-500 text-sm mb-6 max-w-sm">
                                                    {t('b2cDashboard.resumeAnalyzePage.compareDescription', 'Select a job to analyze how well your resume matches the role requirements, identify missing skills, and discover areas for improvement.')}
                                                </p>
                                            </>
                                        )}
                                        {analysis?.job_description_id && (
                                            <h3 className="text-sm font-bold text-gray-900 mb-4 text-left w-full max-w-md">{t('b2cDashboard.resumeAnalyzePage.compareWithAnotherJob', 'Compare with another job:')}</h3>
                                        )}
                                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-md">
                                            <div className="flex-1 w-full sm:w-auto">
                                                <PremiumSelect 
                                                    variant="b2c"
                                                    value={selectedJdId}
                                                    onChange={setSelectedJdId}
                                                    options={jobOptions}
                                                    placeholder={t('b2cDashboard.resumeAnalyzePage.selectJobPlaceholder', '-- Select a Job --')}
                                                    searchable={jobOptions.length > 6}
                                                />
                                            </div>
                                            <button 
                                                onClick={() => handleRunAnalysis(selectedJdId)}
                                                disabled={!selectedJdId || isAnalyzing || selectedJdId === analysis?.job_description_id}
                                                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-5 py-2.5 rounded-xl shadow-sm transition-all disabled:opacity-50 flex items-center justify-center shrink-0"
                                            >
                                                {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                                                {analysis?.job_description_id ? t('b2cDashboard.resumeAnalyzePage.reAnalyzeMatch', 'Re-Analyze Match') : t('b2cDashboard.resumeAnalyzePage.analyzeMatch', 'Analyze Match')}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Analysis Results Area */}
                                    {analysis?.job_description_id && (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center justify-center text-center">
                                                    <h3 className="text-sm font-semibold text-gray-700 mb-4">{t('b2cDashboard.resumeAnalyzePage.jobMatchScore', 'Job Match Score')}</h3>
                                                    <div className="relative inline-flex inline-flex items-center justify-center w-28 h-28 mb-3">
                                                        <CircularProgress variant="determinate" value={100} size={112} thickness={4} sx={{ color: '#f1f5f9', position: 'absolute' }} />
                                                        <CircularProgress variant="determinate" value={jobMatchScore || 0} size={112} thickness={4} sx={{ color: getScoreColor(jobMatchScore || 0), position: 'absolute', strokeLinecap: 'round' }} />
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <span className="text-2xl font-bold text-gray-900">{Math.round(jobMatchScore || 0)}%</span>
                                                        </div>
                                                    </div>
                                                    <div className="font-semibold text-sm" style={{ color: getScoreColor(jobMatchScore || 0) }}>{getScoreLabel(jobMatchScore || 0)}</div>
                                                </div>
                                                <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                                    <h3 className="text-sm font-semibold text-gray-700 mb-4">{t('b2cDashboard.resumeAnalyzePage.skillsMatchDetail', 'Skills Match Detail')}</h3>
                                                    <div className="space-y-3">
                                                        {skillsMatch.length > 0 ? skillsMatch.map((skill, idx) => (
                                                            <div key={idx} className="flex items-center justify-between text-sm">
                                                                <span className="text-gray-700 font-semibold w-1/3">{skill.name}</span>
                                                                <div className="flex-1 mx-4">
                                                                    <CustomLinearProgress value={skill.progress || 0} colorClass={getProgressBarColor(skill.status)} />
                                                                </div>
                                                                <span className={`font-bold text-xs w-16 text-right ${
                                                                    skill.status === 'Matched' ? 'text-green-600' : 
                                                                    skill.status === 'Partial' ? 'text-orange-500' : 'text-red-500'
                                                                }`}>{skill.status}</span>
                                                            </div>
                                                        )) : (
                                                            <p className="text-sm text-gray-500 italic">{t('b2cDashboard.resumeAnalyzePage.noSkillsAnalyzed', 'No specific skills analyzed for this JD.')}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Missing Skills & Recommendations */}
                                            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                                                <div className="flex items-center gap-1.5 mb-5 text-gray-900 font-extrabold text-base">
                                                    {t('b2cDashboard.resumeAnalyzePage.missingSkillsTitle', 'Missing Skills & Recommendations')} <Info className="w-4 h-4 text-gray-400 cursor-help" />
                                                </div>
                                                {missingSkillsData.length > 0 ? (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {missingSkillsData.map((skill, idx) => (
                                                            <div key={idx} className="bg-orange-50/50 border border-orange-100 rounded-xl p-4">
                                                                <div className="font-bold text-sm text-gray-900 mb-1">{skill.name}</div>
                                                                <div className="text-xs text-gray-600 leading-relaxed">{skill.reason}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-gray-500">{t('b2cDashboard.resumeAnalyzePage.allSkillsMatched', 'You matched all the critical skills for this role. Great job!')}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
                </div>
            </PageCard>
        </PageShell>
    );
};

export default ResumeAnalyzePage;
